import { createContext, PropsWithChildren, useContext, useRef, useState } from 'react'
import { ourUuid } from 'utils'
import { useMutation } from 'react-query'
import { GeneratePrompt } from 'types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { generateLinks } from '../generateLinks'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { LinkwithIds } from './Types'

interface ILinksContext {
  abortGeneration: () => void
  generatedLinks: LinkwithIds[]
  generateLinks: (payload: any) => Promise<LinkwithIds[]>
  setGeneratedLinks: React.Dispatch<React.SetStateAction<LinkwithIds[]>>
  isLoading: boolean
  removeLink: (link: LinkwithIds) => void
  error?: boolean
  setError?: React.Dispatch<React.SetStateAction<boolean>>
}

const LinksContext = createContext<ILinksContext>({
  abortGeneration: () => {},
  generatedLinks: [],
  generateLinks: () => Promise.reject('Not implemented'),
  setGeneratedLinks: () => {},
  isLoading: false,
  removeLink: () => {},
  error: false,
  setError: () => {},
})

export const LinksProvider = ({ children }: PropsWithChildren) => {
  const workspaceId = useCurrentWorkspaceId()
  const [generatedLinks, setGeneratedLinks] = useState<LinkwithIds[]>([])
  const [error, setError] = useState<boolean>(false)
  const { mutateAsync, isLoading } = useMutation(generateLinks)
  const abortController = useRef<AbortController | null>(new AbortController())

  const abortGeneration = () => {
    abortController.current?.abort()
    abortController.current = new AbortController()
  }

  const generateLinksHandler = async (payload: any) => {
    const { external_links, ...rest } = payload
    const prompt: GeneratePrompt = { ...rest, generation_type: ChannelType.BlogPost }
    setError(false)

    try {
      const newLinks = await mutateAsync({
        payload: {
          prompt,
          existingLinks: generatedLinks.map((link) => link.url),
          workspaceId: workspaceId,
        },
        signal: abortController.current?.signal,
      })

      const linkOptions: LinkwithIds[] = newLinks.map((link: string) => ({ id: ourUuid(), url: link }))
      setGeneratedLinks((prevLinks) => [...prevLinks, ...linkOptions])
      return linkOptions
    } catch (e) {
      setError(true)
      return []
    }
  }

  const removeLink = (link: LinkwithIds) => {
    setGeneratedLinks((prevLinks) => prevLinks.filter((l) => l.id !== link.id))
  }

  return (
    <LinksContext.Provider
      value={{
        abortGeneration,
        generatedLinks,
        generateLinks: generateLinksHandler,
        setGeneratedLinks,
        isLoading,
        removeLink,
        error,
        setError,
      }}
    >
      {children}
    </LinksContext.Provider>
  )
}

export const useLinks = () => useContext(LinksContext)
